import { lazy, Suspense, useEffect, useState, useSyncExternalStore } from 'react';
import { Icone } from '../Icone';
import { AnaPersonagem } from './AnaPersonagem';

const GavetaSuporte = lazy(() => import('./GavetaSuporte').then((x) => ({ default: x.GavetaSuporte })));

/**
 * Estado da gaveta do suporte, compartilhado pelos gatilhos (topo no PC,
 * botão central da barra no celular, "Abrir conversa" em /ajuda). Uma gaveta
 * só no app; quem abriu recebe o foco de volta ao fechar.
 */
let aberto = false;
let carregar = false;
let origem: HTMLElement | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());
const assinar = (f: () => void) => { ouvintes.add(f); return () => { ouvintes.delete(f); }; };

/** Baixa o código do chat antes do toque (hover/foco no gatilho). */
export function preCarregarSuporte(): void {
  if (!carregar) { carregar = true; avisar(); }
}

export function abrirSuporte(): void {
  origem = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  aberto = true;
  carregar = true;
  avisar();
}

function fecharSuporte(): void {
  aberto = false;
  avisar();
  origem?.focus();
  origem = null;
}

function alternar(): void {
  if (aberto) fecharSuporte(); else abrirSuporte();
}

const useAberto = () => useSyncExternalStore(assinar, () => aberto, () => false);
const useCarregar = () => useSyncExternalStore(assinar, () => carregar, () => false);

/** Monte uma vez no layout: é onde a gaveta vive. */
export function SuporteGaveta({ area }: { area: 'cliente' | 'profissional' }) {
  const estaAberto = useAberto();
  const deveCarregar = useCarregar();
  if (!deveCarregar) return null;
  return (
    <Suspense fallback={null}>
      <GavetaSuporte aberto={estaAberto} area={area} aoFechar={fecharSuporte} />
    </Suspense>
  );
}

/** Atalho "Ajuda" no topo (PC). */
export function BotaoSuporte({ className = '' }: { className?: string }) {
  const estaAberto = useAberto();
  return (
    <button
      type="button"
      className={`acao-topo ${className}`}
      aria-label="Ajuda: falar com a Ana, assistente virtual"
      aria-haspopup="dialog"
      aria-expanded={estaAberto}
      onPointerEnter={preCarregarSuporte}
      onFocus={preCarregarSuporte}
      onClick={alternar}
    >
      <Icone nome="ajuda" /><span>Ajuda</span>
    </button>
  );
}

/**
 * Botão central da barra do celular: círculo escuro saltado da barra com a
 * Ana viva dentro. No toque, ela pula e uma onda sai enquanto a gaveta sobe.
 * Com movimento reduzido, nada pulsa.
 */
export function BotaoAnaBarra() {
  const estaAberto = useAberto();
  const [pulso, setPulso] = useState(0);

  useEffect(() => {
    if (!pulso) return;
    const t = window.setTimeout(() => setPulso(0), 700);
    return () => window.clearTimeout(t);
  }, [pulso]);

  return (
    <button
      type="button"
      className="nav-ana"
      data-pulso={pulso ? '' : undefined}
      data-aberto={estaAberto || undefined}
      aria-label="Ana, assistente virtual: tirar dúvida ou pedir ajuda"
      aria-haspopup="dialog"
      aria-expanded={estaAberto}
      onPointerEnter={preCarregarSuporte}
      onPointerDown={preCarregarSuporte}
      onFocus={preCarregarSuporte}
      onClick={() => { if (!aberto) setPulso(Date.now()); alternar(); }}
    >
      <span className="nav-ana-bolha" aria-hidden="true">
        <span className="nav-ana-onda" key={pulso} />
        <AnaPersonagem tamanho={44} estado={pulso ? 'feliz' : 'parada'} />
      </span>
      <span className="nav-ana-rotulo">Ana</span>
    </button>
  );
}
